import { useState, useCallback, useRef, useEffect } from 'react';

export type StepStatus = 'pending' | 'running' | 'completed' | 'failed';

export interface StepState {
    key: string;
    label: string;
    status: StepStatus;
    error?: string;
}

export type FlowPhase = 'idle' | 'processing' | 'rolling_back' | 'done' | 'failed' | 'rolled_back';

export const CONVERT_STEPS: { key: string; label: string }[] = [
    { key: 'validate', label: 'Validating' },
    { key: 'create_booking', label: 'Creating booking' },
    { key: 'create_order', label: 'Creating order' },
    { key: 'set_booking_form', label: 'Setting booking notes' },
    { key: 'add_line_item', label: 'Adding line item' },
    { key: 'set_line_item_amount', label: 'Setting line item amount' },
    { key: 'add_payment', label: 'Processing payment' },
    { key: 'set_order_notes', label: 'Setting order notes' },
    { key: 'approve_order', label: 'Approving order' },
    { key: 'post_order', label: 'Posting order' },
    { key: 'complete_order', label: 'Completing order' },
    { key: 'allocate', label: 'Allocating to booking' },
    { key: 'confirm_booking', label: 'Confirming booking' },
    { key: 'finalize', label: 'Finalizing voucher' },
];

export const ROLLBACK_STEPS: { key: string; label: string }[] = [
    { key: 'restore_voucher', label: 'Restoring voucher' },
    { key: 'deallocate', label: 'Removing allocation' },
    { key: 'sync', label: 'Syncing changes' },
    { key: 'void_order', label: 'Voiding order' },
    { key: 'cancel_booking', label: 'Cancelling booking' },
];

export interface UseFlowProgressResult {
    steps: StepState[];
    rollbackSteps: StepState[];
    phase: FlowPhase;
    error: string | null;
    startFlow: (flowId: string) => void;
    reset: () => void;
}

export function useFlowProgress(): UseFlowProgressResult {
    const [steps, setSteps] = useState<StepState[]>([]);
    const [rollbackSteps, setRollbackSteps] = useState<StepState[]>([]);
    const [phase, setPhase] = useState<FlowPhase>('idle');
    const [error, setError] = useState<string | null>(null);
    const eventSourceRef = useRef<EventSource | null>(null);

    const attachToFlow = useCallback((flowId: string, initialSteps: StepState[], isRollback = false) => {
        const es = new EventSource(`/api/flows/${flowId}/progress`);
        eventSourceRef.current = es;

        const setCurrentSteps = isRollback ? setRollbackSteps : setSteps;
        let currentSteps = [...initialSteps];

        es.addEventListener('init', (e: MessageEvent) => {
            try {
                const data = JSON.parse(e.data) as {
                    steps: { step: string; status: string; label?: string; error?: string }[];
                };
                currentSteps = currentSteps.map(s => {
                    const serverStep = data.steps.find(ss => ss.step === s.key);
                    if (serverStep) {
                        return {
                            ...s,
                            status: serverStep.status as StepStatus,
                            label: serverStep.label || s.label,
                            error: serverStep.error,
                        };
                    }
                    return s;
                });
                setCurrentSteps([...currentSteps]);
            } catch { /* skip */ }
        });

        es.addEventListener('step', (e: MessageEvent) => {
            try {
                const data = JSON.parse(e.data) as {
                    step: string;
                    status: string;
                    label?: string;
                    error?: string;
                };
                currentSteps = currentSteps.map(s => {
                    if (s.key === data.step) {
                        return {
                            ...s,
                            status: data.status as StepStatus,
                            label: data.label || s.label,
                            error: data.error,
                        };
                    }
                    return s;
                });
                setCurrentSteps([...currentSteps]);
            } catch { /* skip */ }
        });

        es.addEventListener('flow', (e: MessageEvent) => {
            es.close();
            try {
                const data = JSON.parse(e.data) as {
                    status: string;
                    error?: string;
                    rollbackFlowId?: string;
                };

                if (isRollback) {
                    eventSourceRef.current = null;
                    setPhase(data.status === 'completed' ? 'rolled_back' : 'failed');
                } else if (data.status === 'completed') {
                    eventSourceRef.current = null;
                    setPhase('done');
                } else if (data.rollbackFlowId) {
                    if (data.error) setError(data.error);
                    setPhase('rolling_back');
                    const rbSteps: StepState[] = ROLLBACK_STEPS.map(s => ({
                        key: s.key, label: s.label, status: 'pending' as StepStatus,
                    }));
                    setRollbackSteps(rbSteps);
                    attachToFlow(data.rollbackFlowId, rbSteps, true);
                } else {
                    eventSourceRef.current = null;
                    if (data.error) setError(data.error);
                    setPhase('failed');
                }
            } catch {
                eventSourceRef.current = null;
                setPhase('failed');
            }
        });

        es.onerror = () => {
            if (es.readyState === EventSource.CLOSED) {
                setError('Lost connection to server');
                setPhase('failed');
                eventSourceRef.current = null;
            }
        };
    }, []);

    const startFlow = useCallback((flowId: string) => {
        const initialSteps: StepState[] = CONVERT_STEPS.map(s => ({
            key: s.key, label: s.label, status: 'pending' as StepStatus,
        }));
        setSteps(initialSteps);
        setRollbackSteps([]);
        setPhase('processing');
        setError(null);
        attachToFlow(flowId, initialSteps);
    }, [attachToFlow]);

    const reset = useCallback(() => {
        eventSourceRef.current?.close();
        eventSourceRef.current = null;
        setSteps([]);
        setRollbackSteps([]);
        setPhase('idle');
        setError(null);
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            eventSourceRef.current?.close();
        };
    }, []);

    return { steps, rollbackSteps, phase, error, startFlow, reset };
}
